import type { FacilityReviewStatus, FacilitySegment } from "./types";

export const SEGMENTS: readonly FacilitySegment[] = [
  "Producer",
  "Compounder",
  "Converter",
  "Distributor",
  "Recycler",
  "Unclassified",
];

export const REVIEW_STATUSES: readonly FacilityReviewStatus[] = ["Verified", "Rejected", "Unclassified"];
export const DEFAULT_REVIEW_STATUSES: readonly FacilityReviewStatus[] = ["Verified", "Unclassified"];

export type CandidateMode = "exclude" | "include" | "only";

export interface Bbox {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

export interface FacilityFilters {
  state: string;
  segment: FacilitySegment | "";
  naics: string;
  q: string;
  unmatched: boolean;
  candidates: CandidateMode;
  bbox: Bbox | null;
  reviewStatuses: readonly FacilityReviewStatus[];
}

export const EMPTY_FILTERS: FacilityFilters = {
  state: "",
  segment: "",
  naics: "",
  q: "",
  unmatched: false,
  candidates: "exclude",
  bbox: null,
  reviewStatuses: DEFAULT_REVIEW_STATUSES,
};

const STATE_PATTERN = /^[A-Za-z]{2}$/;
const NAICS_PATTERN = /^\d{2,6}$/;
const MIN_SEARCH_LENGTH = 2;
const BBOX_DECIMALS = 4;

export function formatBbox(bbox: Bbox): string {
  return [bbox.minLon, bbox.minLat, bbox.maxLon, bbox.maxLat].map((n) => n.toFixed(BBOX_DECIMALS)).join(",");
}

export function isDefaultReview(statuses: readonly FacilityReviewStatus[]): boolean {
  return (
    statuses.length === DEFAULT_REVIEW_STATUSES.length &&
    DEFAULT_REVIEW_STATUSES.every((status) => statuses.includes(status))
  );
}

export function toggleReviewStatus(
  current: readonly FacilityReviewStatus[],
  status: FacilityReviewStatus | "All",
): readonly FacilityReviewStatus[] {
  if (status === "All") {
    return current.length === REVIEW_STATUSES.length ? DEFAULT_REVIEW_STATUSES : REVIEW_STATUSES;
  }
  if (!current.includes(status)) return REVIEW_STATUSES.filter((s) => s === status || current.includes(s));
  if (current.length === 1) return current;
  return current.filter((s) => s !== status);
}

export function toParams(filters: FacilityFilters): URLSearchParams {
  const params = new URLSearchParams();
  const state = filters.state.trim();
  const naics = filters.naics.trim();
  const q = filters.q.trim();
  if (STATE_PATTERN.test(state)) params.set("state", state.toUpperCase());
  if (filters.segment) params.set("segment", filters.segment);
  if (NAICS_PATTERN.test(naics)) params.set("naics", naics);
  if (q.length >= MIN_SEARCH_LENGTH) params.set("q", q);
  if (filters.unmatched) params.set("unmatched", "true");
  if (filters.candidates !== "exclude") params.set("candidates", filters.candidates);
  if (filters.bbox) params.set("bbox", formatBbox(filters.bbox));
  if (!isDefaultReview(filters.reviewStatuses)) {
    for (const status of REVIEW_STATUSES.filter((s) => filters.reviewStatuses.includes(s))) {
      params.append("reviewStatus", status);
    }
  }
  return params;
}

export function listPath(filters: FacilityFilters, limit: number, offset: number): string {
  const params = toParams(filters);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return `facilities?${params.toString()}`;
}

export function pointsPath(filters: FacilityFilters): string {
  return `facilities/points?${toParams(filters).toString()}`;
}

export function activeFilterCount(filters: FacilityFilters): number {
  return [...toParams(filters).keys()].length;
}

export function pageRange(total: number, limit: number, offset: number): string {
  if (total === 0) return "0 facilities";
  const first = offset + 1;
  const last = Math.min(offset + limit, total);
  return `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`;
}
